import { useRepo } from 'pinia-orm'
import { computed } from 'vue'
import ImpactReason from '../../store/models/ImpactReason'
import { value } from '@primeuix/themes/aura/knob'
import Child from '../../store/models/Child'

export const useImpactReason = (child: Child | null) => {
  const impactRepo = useRepo(ImpactReason)
  const childRepo = useRepo(Child)

  const increases = computed(() => {
    if (!child) {
      return []
    }
    return impactRepo
      .where((record) => {
        return record.isIncrease === true && record.childId === child.id
      })
      .get()
  })

  const decreases = computed(() => {
    if (!child) {
      return []
    }
    return impactRepo
      .where((record) => {
        return record.isIncrease === false && record.childId === child.id
      })
      .get()
  })

  const commonImpacts = computed(() => {
    if (!child) {
      return []
    }
    const childRecord = childRepo.with('screenTimeEvents').find(child.id)

    if (!childRecord || !childRecord.screenTimeEvents) {
      return []
    }

    const counts = childRecord.screenTimeEvents.reduce((acc, event) => {
      const impactReason = impactRepo
        .where((record) => record.description === event.description)
        .first()
      if (impactReason) {
        acc[impactReason.id] = (acc[impactReason.id] || 0) + 1
      }
      return acc
    }, {})

    const averageImpact = childRecord.screenTimeEvents.reduce((acc, event) => {
      const impactReason = impactRepo
        .where((record) => record.description === event.description)
        .first()
      if (impactReason) {
        acc[impactReason.id] = (acc[impactReason.id] || 0) + event.impact
      }
      return acc
    }, {})
    // Calculate the average impact for each reason
    for (const id in averageImpact) {
      averageImpact[id] /= counts[id]
      averageImpact[id] = Math.round(averageImpact[id] / 15) * 15
    }

    const countsA = Object.entries(counts)

    const sortedImpacts = countsA.map(([id, count]) => {
      const impactReason = impactRepo.find(id)
      return {
        id,
        count,
        description: impactReason?.description,
        impact: averageImpact[id],
      } as {
        id: string
        count: number
        description: string
        impact: number
      }
    })
    return sortedImpacts
  })

  const commonIncreases = computed(() => {
    if (!child) {
      return []
    }
    const increases = commonImpacts.value.filter((impact) => impact.impact > 0)
    const sortedIncreases = increases.sort((a, b) => b.count - a.count)
    return sortedIncreases
  })

  const commonDecreases = computed(() => {
    if (!child) {
      return []
    }
    const decreases = commonImpacts.value.filter((impact) => impact.impact < 0)
    const sortedDecreases = decreases.sort((a, b) => b.count - a.count)
    return sortedDecreases
  })

  return {
    increases,
    decreases,
    impactRepo,
    commonIncreases,
    commonDecreases,
  }
}
